import React, { useState, useEffect, useContext, useMemo } from "react";
import { collection, getDocs, query, orderBy, where } from "firebase/firestore";
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaSearch,
  FaSpinner,
  FaExternalLinkAlt,
  FaPhone,
  FaGlobe,
  FaFilter,
  FaEye,
  FaRoute,
  FaTimes,
  FaIndustry,
  FaRecycle,
  FaLinkedin,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";
import { getAuth } from "firebase/auth";
import CollectorResearchMap from "./CollectorResearchMap";

// Helper function to calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Radius of Earth in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Geocode address using Google Maps API
const geocodeAddress = async (address, apiKey) => {
  if (!address || !apiKey) return null;

  try {
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${apiKey}`;

    const response = await fetch(geocodeUrl);
    const data = await response.json();

    if (data.status === "OK" && data.results?.[0]?.geometry?.location) {
      return data.results[0].geometry.location;
    }
  } catch (error) {
    console.error("Geocoding error:", error);
  }

  return null;
};

const CollectorResearch = ({ teamId }) => {
  const { user } = useContext(AuthContext);
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  // State management
  const [collectors, setCollectors] = useState([]);
  const [researchCompanies, setResearchCompanies] = useState([]);
  const [nearbyCompanies, setNearbyCompanies] = useState([]);
  const [selectedCollector, setSelectedCollector] = useState(null);

  // Loading states
  const [loadingCollectors, setLoadingCollectors] = useState(true);
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [searchingNearby, setSearchingNearby] = useState(false);

  // Search and filter states
  const [searchRadius, setSearchRadius] = useState(100); // miles
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterEmployeeCount, setFilterEmployeeCount] = useState("");
  const [filterKeyAccountOnly, setFilterKeyAccountOnly] = useState(false);
  const [showMap, setShowMap] = useState(true);

  // Modal state for company details
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Load collectors from Firestore (with JSON fallback)
  useEffect(() => {
    if (!user || !teamId) return;

    const loadCollectors = async () => {
      try {
        setLoadingCollectors(true);
        console.log("Loading collectors for team:", teamId);

        // Try Firestore first
        const collectorsRef = collection(db, "teams", teamId, "collectors");
        const snap = await getDocs(query(collectorsRef, orderBy("name")));

        if (!snap.empty) {
          const collectorsData = [];
          snap.forEach((doc) => {
            collectorsData.push({ id: doc.id, ...doc.data() });
          });

          console.log(
            "Loaded collectors from Firestore:",
            collectorsData.length
          );
          setCollectors(collectorsData);
          console.log(
            "Sample collector IDs:",
            collectorsData.slice(0, 3).map((c) => ({ id: c.id, name: c.name }))
          );
          toast.success(
            `Loaded ${collectorsData.length} collector facilities from database`
          );
        } else {
          // Fallback to JSON
          console.log("No Firestore collectors found, trying JSON fallback");
          const response = await fetch("/collectors.json");
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const collectorsData = data.facilities || [];

          console.log("Loaded collectors from JSON:", collectorsData.length);
          setCollectors(collectorsData);
          toast.info(
            `Loaded ${collectorsData.length} collector facilities from JSON`
          );
        }
      } catch (error) {
        console.error("Error loading collectors:", error);
        toast.error(`Failed to load collectors: ${error.message}`);
      } finally {
        setLoadingCollectors(false);
      }
    };

    loadCollectors();
  }, [user, teamId]);

  // Load research companies
  useEffect(() => {
    if (!user || !teamId) return;

    const loadResearch = async () => {
      try {
        setLoadingResearch(true);
        const researchRef = collection(db, "teams", teamId, "research");
        const snap = await getDocs(query(researchRef, orderBy("companyName")));

        const researchData = [];
        snap.forEach((doc) => {
          researchData.push({ id: doc.id, ...doc.data() });
        });

        setResearchCompanies(researchData);
        console.log(`Loaded ${researchData.length} research companies`);
      } catch (error) {
        console.error("Error loading research:", error);
        toast.error(`Failed to load research data: ${error.message}`);
      } finally {
        setLoadingResearch(false);
      }
    };

    loadResearch();
  }, [user, teamId]);

  // Find nearby research companies using backend API
  const findNearbyCompanies = async (collector) => {
    console.log("Finding nearby companies for collector:", collector);

    if (!collector || !collector.coordinates) {
      console.error("Collector missing coordinates:", collector);
      return toast.error("Collector coordinates not available");
    }

    setSearchingNearby(true);
    toast.info(`Searching for companies near ${collector.name}...`);

    try {
      // Get user token for authentication
      const token = user ? await getAuth().currentUser.getIdToken(true) : null;
      if (!token) {
        throw new Error("Authentication required");
      }

      // Prepare API request (consistent with other components)
      const apiBase =
        window.location.hostname.includes("localhost") ||
        window.location.hostname.includes("127.0.0.1")
          ? "http://127.0.0.1:5001/damia-gcp/us-central1/api"
          : "https://us-central1-damia-gcp.cloudfunctions.net/api";

      const requestBody = {
        teamId,
        collectorId: collector.id, // This should be the Firestore document ID like "collector-66"
        searchRadius,
        filters: {
          industry: filterIndustry,
          region: filterRegion,
        },
      };

      console.log("Collector object:", collector);
      console.log("Collector ID being sent:", collector.id);

      console.log("API request:", requestBody);

      // Call backend API
      const response = await fetch(`${apiBase}/collectorProximitySearch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      console.log("API response:", data);

      // Update state with results
      setNearbyCompanies(data.nearbyCompanies || []);

      if (data.nearbyCompanies?.length > 0) {
        toast.success(
          `Found ${data.nearbyCompanies.length} research companies within ${searchRadius} miles of ${collector.name}`
        );
      } else {
        toast.warning(
          `No research companies found within ${searchRadius} miles of ${collector.name}. Try increasing the search radius.`
        );
      }

      // Log statistics
      if (data.stats) {
        console.log("Search statistics:", data.stats);
      }
    } catch (error) {
      console.error("Error finding nearby companies:", error);
      toast.error(`Failed to find nearby companies: ${error.message}`);
    } finally {
      setSearchingNearby(false);
    }
  };

  // Filter nearby companies based on criteria
  const filteredNearby = useMemo(() => {
    let filtered = [...nearbyCompanies];

    if (filterIndustry) {
      filtered = filtered.filter((company) =>
        company.industrySegment
          ?.toLowerCase()
          .includes(filterIndustry.toLowerCase())
      );
    }

    if (filterRegion) {
      filtered = filtered.filter((company) =>
        company.region?.toLowerCase().includes(filterRegion.toLowerCase())
      );
    }

    if (filterEmployeeCount) {
      const minEmployees = parseInt(filterEmployeeCount);
      filtered = filtered.filter((company) => {
        const empCount = company.facilityInfo?.employeeCount || 0;
        return empCount >= minEmployees;
      });
    }

    if (filterKeyAccountOnly) {
      filtered = filtered.filter((company) => company.keyNationalAccount);
    }

    return filtered;
  }, [
    nearbyCompanies,
    filterIndustry,
    filterRegion,
    filterEmployeeCount,
    filterKeyAccountOnly,
  ]);

  // Get unique industries and regions for filters
  const uniqueIndustries = [
    ...new Set(nearbyCompanies.map((c) => c.industrySegment).filter(Boolean)),
  ].sort();
  const uniqueRegions = [
    ...new Set(nearbyCompanies.map((c) => c.region).filter(Boolean)),
  ].sort();

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Collector - Company Research
          </h1>
          <p className="text-gray-600">
            Find research companies near collector facilities for targeted
            outreach
          </p>
        </div>
        <button
          onClick={() => setShowMap(!showMap)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <FaMapMarkerAlt />
          <span>{showMap ? "Hide Map" : "Show Map"}</span>
        </button>
      </div>

      {/* Collector Selection */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <FaBuilding className="mr-2 text-blue-600" />
          Step 1: Select Collector Facility
        </h2>

        {loadingCollectors ? (
          <div className="flex items-center text-gray-500">
            <FaSpinner className="animate-spin mr-2" />
            Loading collectors...
          </div>
        ) : collectors.length === 0 ? (
          <div className="text-center py-8">
            <FaBuilding className="text-4xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No Collectors Found
            </h3>
            <p className="text-gray-500">
              No collector facilities found for this team. Check your team
              permissions or data migration.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose Collector Facility
              </label>
              <select
                value={selectedCollector?.id || ""}
                onChange={(e) => {
                  console.log("Dropdown changed to:", e.target.value);
                  console.log(
                    "Available collectors:",
                    collectors.map((c) => ({ id: c.id, name: c.name }))
                  );

                  if (e.target.value === "") {
                    setSelectedCollector(null);
                    setNearbyCompanies([]);
                    return;
                  }

                  // Try different ID formats since Firestore doc IDs might be different
                  let collector = collectors.find(
                    (c) => c.id === e.target.value
                  );

                  // If not found, try matching by the original ID field
                  if (!collector) {
                    collector = collectors.find(
                      (c) => String(c.id) === e.target.value
                    );
                  }

                  // If still not found, try matching by the original facility ID
                  if (!collector) {
                    collector = collectors.find(
                      (c) =>
                        String(c.originalId || c.facilityId) === e.target.value
                    );
                  }

                  console.log("Found collector:", collector);

                  if (collector) {
                    setSelectedCollector(collector);
                    toast.success(`Selected: ${collector.name}`);
                  } else {
                    console.error(
                      "Collector not found for ID:",
                      e.target.value
                    );
                    console.error(
                      "Available IDs:",
                      collectors.map((c) => c.id)
                    );
                  }
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a collector facility...</option>
                {collectors.map((collector) => (
                  <option key={collector.id} value={collector.id}>
                    {collector.name} - {collector.city}, {collector.state}
                  </option>
                ))}
              </select>

              {/* Debug info */}
              <div className="mt-2 text-xs text-gray-500">
                {collectors.length} facilities loaded | Selected:{" "}
                {selectedCollector?.name || "None"}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Radius (miles)
              </label>
              <input
                type="number"
                value={searchRadius}
                onChange={(e) => setSearchRadius(Number(e.target.value))}
                min="10"
                max="500"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {selectedCollector && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">
              Selected Collector:
            </h3>
            <div className="text-sm text-blue-800">
              <div>
                <strong>{selectedCollector.name}</strong>
              </div>
              <div>{selectedCollector.address}</div>
              <div>
                {selectedCollector.city}, {selectedCollector.state}{" "}
                {selectedCollector.zip}
              </div>
              <div>Type: {selectedCollector.facility_type}</div>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Show Collector on Map and Search for Companies */}
      {selectedCollector && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <FaSearch className="mr-2 text-green-600" />
            Step 2: Find Nearby Companies
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Collector Map */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">
                Selected Collector Location
              </h3>
              <div className="h-64 rounded-lg overflow-hidden border">
                <CollectorResearchMap
                  collector={selectedCollector}
                  researchCompanies={[]} // Show only collector initially
                  searchRadius={searchRadius}
                  onCompanySelect={() => {}}
                />
              </div>
            </div>

            {/* Search Controls */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">
                Search Parameters
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Radius: {searchRadius} miles
                  </label>
                  <input
                    type="range"
                    min="25"
                    max="500"
                    step="25"
                    value={searchRadius}
                    onChange={(e) => setSearchRadius(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>25 mi</span>
                    <span>500 mi</span>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">
                    Search Info
                  </h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>• Collector: {selectedCollector.name}</div>
                    <div>
                      • Location: {selectedCollector.city},{" "}
                      {selectedCollector.state}
                    </div>
                    <div>• Search Radius: {searchRadius} miles</div>
                    <div>
                      • Available Research Companies: {researchCompanies.length}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => findNearbyCompanies(selectedCollector)}
                  disabled={searchingNearby}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  {searchingNearby ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <FaSearch className="mr-2" />
                      Find Companies Within {searchRadius} Miles
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Search Results */}
      {selectedCollector && nearbyCompanies.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Results Header */}
          <div className="p-6 border-b">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold flex items-center">
                <FaEye className="mr-2 text-purple-600" />
                Step 3: Research Results - {nearbyCompanies.length} Companies
                Found
              </h2>
              <span className="text-sm text-gray-500">
                Within {searchRadius} miles of {selectedCollector.name}
              </span>
            </div>

            {/* Enhanced Filters */}
            {nearbyCompanies.length > 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter by Industry
                    </label>
                    <select
                      value={filterIndustry}
                      onChange={(e) => setFilterIndustry(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Industries</option>
                      {uniqueIndustries.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter by Region
                    </label>
                    <select
                      value={filterRegion}
                      onChange={(e) => setFilterRegion(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Regions</option>
                      {uniqueRegions.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Employee Count
                    </label>
                    <select
                      value={filterEmployeeCount}
                      onChange={(e) => setFilterEmployeeCount(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Any Size</option>
                      <option value="50">50+ employees</option>
                      <option value="100">100+ employees</option>
                      <option value="250">250+ employees</option>
                      <option value="500">500+ employees</option>
                      <option value="1000">1000+ employees</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setFilterIndustry("");
                        setFilterRegion("");
                        setFilterEmployeeCount("");
                        setFilterKeyAccountOnly(false);
                      }}
                      className="w-full p-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center justify-center"
                    >
                      <FaFilter className="mr-2" />
                      Clear Filters
                    </button>
                  </div>
                </div>

                {/* Key Account Filter */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="keyAccountFilter"
                    checked={filterKeyAccountOnly}
                    onChange={(e) => setFilterKeyAccountOnly(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="keyAccountFilter"
                    className="text-sm text-gray-700"
                  >
                    Show only Key National Accounts
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Results Content */}
          <div className="p-6">
            {nearbyCompanies.length === 0 &&
              !searchingNearby &&
              selectedCollector && (
                <div className="text-center py-8">
                  <FaMapMarkerAlt className="text-4xl text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    No Research Companies Found
                  </h3>
                  <p className="text-gray-500">
                    No research companies found within {searchRadius} miles of{" "}
                    {selectedCollector.name}. Try increasing the search radius.
                  </p>
                </div>
              )}

            {filteredNearby.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">
                    {filteredNearby.length} Companies Found
                    {filterIndustry || filterRegion ? ` (filtered)` : ""}
                  </h3>
                  <span className="text-sm text-gray-500">
                    Within {searchRadius} miles of {selectedCollector.name}
                  </span>
                </div>

                {/* Companies Table */}
                <div className="overflow-hidden border rounded-lg">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Distance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Industry
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Territory
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredNearby.map((company) => (
                        <tr key={company.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900">
                                {company.companyName}
                              </div>
                              {company.keyNationalAccount && (
                                <span className="inline-block px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded mt-1">
                                  Key Account
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div>
                                {company.location?.city},{" "}
                                {company.location?.state}
                              </div>
                              <div className="text-gray-500">
                                {company.location?.zipCode}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <FaRoute className="text-gray-400 mr-1" />
                              <span className="text-sm font-medium">
                                {company.distance} mi
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">
                              {company.industrySegment || "—"}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">
                              {company.territoryOwner || "—"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => {
                                  const address =
                                    company.location?.full ||
                                    `${company.location?.street || ""} ${
                                      company.location?.city || ""
                                    } ${company.location?.state || ""} ${
                                      company.location?.zipCode || ""
                                    }`.trim();
                                  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                    address
                                  )}`;
                                  window.open(url, "_blank");
                                }}
                                className="bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 flex items-center text-sm"
                              >
                                <FaMapMarkerAlt className="mr-1" />
                                Map
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedCompany(company);
                                  setShowDetailModal(true);
                                }}
                                className="bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200 flex items-center text-sm"
                              >
                                <FaEye className="mr-1" />
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Combined Map View */}
      {showMap && selectedCollector && nearbyCompanies.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Combined Map View</h3>
            <p className="text-sm text-gray-600">
              Red marker: {selectedCollector.name} | Blue markers: Research
              companies | Gold markers: Key accounts
            </p>
          </div>
          <CollectorResearchMap
            collector={selectedCollector}
            researchCompanies={filteredNearby}
            searchRadius={searchRadius}
            onCompanySelect={(company) => {
              toast.info(`Selected: ${company.companyName}`);
            }}
          />
        </div>
      )}

      {/* Summary Stats */}
      {selectedCollector && nearbyCompanies.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-blue-600">
              {nearbyCompanies.length}
            </div>
            <div className="text-sm text-gray-600">Total Companies</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-green-600">
              {nearbyCompanies.filter((c) => c.keyNationalAccount).length}
            </div>
            <div className="text-sm text-gray-600">Key Accounts</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-purple-600">
              {uniqueIndustries.length}
            </div>
            <div className="text-sm text-gray-600">Industries</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-orange-600">
              {nearbyCompanies.filter((c) => c.distance <= 25).length}
            </div>
            <div className="text-sm text-gray-600">Within 25 Miles</div>
          </div>
        </div>
      )}

      {/* Company Details Modal */}
      {showDetailModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">
                    {selectedCompany.companyName}
                  </h2>
                  <p className="text-blue-100 mt-1">
                    {selectedCompany.distance} miles from{" "}
                    {selectedCollector.name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedCompany(null);
                  }}
                  className="text-white hover:text-gray-200 text-2xl"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <FaBuilding className="mr-2 text-blue-600" />
                    Company Information
                  </h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600">Company Name</dt>
                      <dd className="font-medium">
                        {selectedCompany.companyName}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">
                        Industry Segment
                      </dt>
                      <dd className="font-medium">
                        {selectedCompany.industrySegment || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Region</dt>
                      <dd className="font-medium">
                        {selectedCompany.region || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Territory Owner</dt>
                      <dd className="font-medium">
                        {selectedCompany.territoryOwner || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">
                        Key National Account
                      </dt>
                      <dd className="font-medium">
                        {selectedCompany.keyNationalAccount ? (
                          <span className="text-green-600 font-semibold">
                            ✓ Yes
                          </span>
                        ) : (
                          <span className="text-gray-500">No</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Location Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-green-600" />
                    Location & Proximity
                  </h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600">
                        Distance from Collector
                      </dt>
                      <dd className="font-medium text-lg text-blue-600">
                        {selectedCompany.distance} miles
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Address</dt>
                      <dd className="font-medium">
                        {selectedCompany.location?.street || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">City</dt>
                      <dd className="font-medium">
                        {selectedCompany.location?.city || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">State</dt>
                      <dd className="font-medium">
                        {selectedCompany.location?.state || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">ZIP Code</dt>
                      <dd className="font-medium">
                        {selectedCompany.location?.zipCode || "—"}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Facility Information */}
              {selectedCompany.facilityInfo && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <FaIndustry className="mr-2 text-purple-600" />
                    Facility Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm text-gray-600">Employee Count</dt>
                      <dd className="text-2xl font-bold text-purple-600">
                        {selectedCompany.facilityInfo.employeeCount || "—"}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm text-gray-600">Square Feet</dt>
                      <dd className="text-2xl font-bold text-green-600">
                        {selectedCompany.facilityInfo.squareFeet
                          ? selectedCompany.facilityInfo.squareFeet.toLocaleString()
                          : "—"}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm text-gray-600">Facility Type</dt>
                      <dd className="text-lg font-medium text-blue-600">
                        {selectedCompany.facilityInfo.facilityType || "—"}
                      </dd>
                    </div>
                  </div>
                </div>
              )}

              {/* Waste Metrics */}
              {selectedCompany.wasteMetrics && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <FaRecycle className="mr-2 text-green-600" />
                    Waste Generation Profile
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <dt className="text-sm text-gray-600">Total Tons/Year</dt>
                      <dd className="text-2xl font-bold text-green-600">
                        {selectedCompany.wasteMetrics.totalTons
                          ? selectedCompany.wasteMetrics.totalTons.toLocaleString()
                          : "—"}
                      </dd>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <dt className="text-sm text-gray-600">Waste Types</dt>
                      <dd className="text-sm text-gray-700">
                        {selectedCompany.wasteMetrics.wasteTypes?.join(", ") ||
                          "—"}
                      </dd>
                    </div>
                  </div>
                </div>
              )}

              {/* Enrichment Data */}
              {selectedCompany.enrichment && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <FaGlobe className="mr-2 text-blue-600" />
                    Company Enrichment
                  </h3>
                  <div className="space-y-4">
                    {selectedCompany.enrichment.companySummary && (
                      <div>
                        <dt className="text-sm font-medium text-gray-700">
                          Company Summary
                        </dt>
                        <dd className="text-sm text-gray-600 mt-1 p-3 bg-gray-50 rounded">
                          {selectedCompany.enrichment.companySummary}
                        </dd>
                      </div>
                    )}
                    {selectedCompany.enrichment.website && (
                      <div>
                        <dt className="text-sm font-medium text-gray-700">
                          Website
                        </dt>
                        <dd className="mt-1">
                          <a
                            href={selectedCompany.enrichment.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <FaExternalLinkAlt className="mr-1" />
                            {selectedCompany.enrichment.website}
                          </a>
                        </dd>
                      </div>
                    )}
                    {selectedCompany.enrichment.linkedinUrl && (
                      <div>
                        <dt className="text-sm font-medium text-gray-700">
                          LinkedIn
                        </dt>
                        <dd className="mt-1">
                          <a
                            href={selectedCompany.enrichment.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <FaLinkedin className="mr-1" />
                            View LinkedIn Profile
                          </a>
                        </dd>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    selectedCompany.location?.full ||
                      `${selectedCompany.location?.street || ""} ${
                        selectedCompany.location?.city || ""
                      } ${selectedCompany.location?.state || ""} ${
                        selectedCompany.location?.zipCode || ""
                      }`.trim()
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <FaMapMarkerAlt className="mr-2" />
                  View on Google Maps
                </a>
                <a
                  href={`https://www.google.com/maps/dir/${selectedCollector.coordinates.latitude},${selectedCollector.coordinates.longitude}/${selectedCompany.coordinates.latitude},${selectedCompany.coordinates.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                >
                  <FaRoute className="mr-2" />
                  Get Directions ({selectedCompany.distance} mi)
                </a>
                {selectedCompany.enrichment?.website && (
                  <a
                    href={selectedCompany.enrichment.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
                  >
                    <FaGlobe className="mr-2" />
                    Visit Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectorResearch;
